import { Button, Spin, Tabs, Typography } from 'antd';
import { ReactNode, useMemo, useState } from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import { useAppSelector } from 'store/hooks';
import { selectAnalyticsLoading } from 'store/analytics/selectors';
import { useOrganizationsStateContext } from 'providers/Organizations';
import { usePermission } from 'hooks';

import { PageWrapper } from 'components/ui';
import { CommissionSwitcher, DashboardReview, Dynamics } from './components';
import { StyledSpinner } from './styled';
import { AnalyticsTabsEnum } from './_types';
import { downloadDynamicsCSV } from './_utils';

const PartnerAnalytics = () => {
  const { parentSelected } = useOrganizationsStateContext();

  const [activeKey, setActiveKey] = useState(AnalyticsTabsEnum.review);
  const { isSuperAdmin } = usePermission();

  const loading = useAppSelector(selectAnalyticsLoading);

  const utmSource = useMemo(
    () => (parentSelected === 'all' ? undefined : parentSelected),
    [parentSelected]
  );

  const renderContent = () => {
    switch (activeKey) {
      case AnalyticsTabsEnum.review:
        return <DashboardReview utmSource={utmSource} />;
      case AnalyticsTabsEnum.dynamics:
        return <Dynamics utmSource={utmSource} />;
      default:
        return null;
    }
  };

  const onTabChange = (key: AnalyticsTabsEnum) => {
    setActiveKey(key);
  };

  const renderExtra = () => {
    const results: ReactNode[] = [];

    if (isSuperAdmin) {
      results.push(<CommissionSwitcher key="CommissionSwitcher" />);
    }

    if (activeKey === AnalyticsTabsEnum.dynamics) {
      results.push(
        <Button
          key="export-data"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => downloadDynamicsCSV(parentSelected)}
        >
          Export Data
        </Button>
      );
    }

    return results;
  };

  return (
    <PageWrapper
      color="white"
      title="Analytics"
      back
      stickyHeader
      extra={renderExtra()}
    >
      <div style={{ position: 'relative' }}>
        <Tabs
          defaultActiveKey={activeKey}
          onChange={key => onTabChange(key as AnalyticsTabsEnum)}
          items={[
            {
              key: AnalyticsTabsEnum.review,
              label: 'Dashboard Review'
            },
            {
              key: AnalyticsTabsEnum.dynamics,
              label: 'Dynamics'
            }
          ]}
        />
        {renderContent()}
        {loading && (
          <StyledSpinner justify="center" align="center" gap={24} vertical>
            <Spin size="large" />
            <Typography.Title level={4}>
              Fetching analytics data…
            </Typography.Title>
          </StyledSpinner>
        )}
      </div>
    </PageWrapper>
  );
};

export default PartnerAnalytics;
